/*
  Stage drawer (UI overhaul wave 2, screen 04 idea 4; bottom sheet on phones, idea 10).

  Opens from a stage row (or "Update <next stage>"). Everything is a draft until Save: the stage status,
  each item (labelled Name / Status / Date / Reference / Note), extra fields and sub-items. One Save sends
  it all in one request (one history entry → one Undo); Cancel / × / Escape ask before dropping changes
  (SidePanel). Removing an item shows "Item removed · Undo" in the drawer — nothing is lost until Save.

  Props: open, onOpenChange, stage, stageNumber, lines (the saved items), statusOptions, lineStatusOptions,
  supportsChildren, onSave({ status?, lines? }) (async; throw to keep the drawer open), onCreateTask(line),
  onOpenReference(reference), taskLineIds (Set), canEdit, startWith ('add-item'), focusLineId, phone.
*/
import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRight, ChevronUp, Copy, CornerDownRight, ExternalLink, ListTodo, Plus, SquarePlus, Trash2, Undo2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SidePanel, SidePanelSection } from '@/components/ui/side-panel';
import { StatusPill } from '@/components/ui/status-pill';
import { RowActions } from '@/components/ui/row-actions';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/format';
import {
  cloneStageLines, countStageDraftChanges, duplicateStageLine, isBlankStageLine, linesForSave, newStageExtraField, newStageLine,
} from './stagesModel';

const displayStatus = (status) => (status === 'Completed' ? 'Approved' : (status || 'Not Started'));
const FIELD_LABEL = 'text-[12px] font-semibold text-ink-2';

function Field({ label, htmlFor, hint, changed, children, className }) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      <label htmlFor={htmlFor} className={cn(FIELD_LABEL, 'flex items-center gap-1.5')}>
        {label}
        {changed ? (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-status-amber-ink">
            <span className="ui-tone-dot tone-amber h-1.5 w-1.5" aria-hidden="true" />changed
          </span>
        ) : null}
        {hint ? <span className="ml-auto text-[11.5px] font-medium text-ink-3">{hint}</span> : null}
      </label>
      {children}
    </div>
  );
}

function ItemEditor({
  line, index, isSubItem, original, canEdit, lineStatusOptions, onChange, onOpenReference, menuItems, children,
}) {
  const id = `stage-item-${line.id}`;
  const changed = (field) => Boolean(original) && String(original[field] ?? '') !== String(line[field] ?? '');
  const extraFields = line.extra_fields || [];
  const setField = (field, value) => onChange({ ...line, [field]: value });
  const setExtra = (fieldId, key, value) => onChange({
    ...line,
    extra_fields: extraFields.map((field) => (field.id === fieldId ? { ...field, [key]: value } : field)),
  });
  return (
    <div
      className={cn(
        'rounded-[14px] border border-line bg-surface p-3.5 sm:p-4',
        isSubItem && 'border-dashed bg-[color:color-mix(in_srgb,var(--surface-2)_60%,transparent)] p-3',
        !original && !isBlankStageLine(line) && 'border-[color:color-mix(in_srgb,var(--status-amber)_50%,transparent)]',
      )}
      data-testid={isSubItem ? `stage-sub-item-${line.id}` : `stage-item-${line.id}`}
    >
      <div className="mb-3 flex items-center gap-2">
        <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">
          {isSubItem ? `Sub-item ${index + 1}` : `Item ${index + 1}`}
        </span>
        {!original ? <span className="text-[11px] font-semibold text-status-amber-ink">new</span> : null}
        <StatusPill status={line.status || 'In Progress'} domain="stage_line" size="sm" className="ml-auto" />
        {menuItems?.some(Boolean) ? (
          <RowActions
            size="sm"
            label={`Actions for ${line.name || (isSubItem ? 'sub-item' : `item ${index + 1}`)}`}
            data-testid={isSubItem ? `stage-sub-line-actions-${line.id}` : `stage-line-actions-${line.id}`}
            items={menuItems}
          />
        ) : null}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Name" htmlFor={`${id}-name`} changed={changed('name')} className="sm:col-span-2">
          <Input
            id={`${id}-name`}
            value={line.name || ''}
            onChange={(event) => setField('name', event.target.value)}
            placeholder={isSubItem ? 'Sub-item name' : 'What is this item? e.g. KNX devices — material submittal'}
            disabled={!canEdit}
            data-stage-line-name={line.id}
          />
        </Field>
        <Field label="Status" htmlFor={`${id}-status`} changed={changed('status')}>
          <Select value={line.status || 'In Progress'} onValueChange={(value) => setField('status', value)} disabled={!canEdit}>
            <SelectTrigger id={`${id}-status`} aria-label="Item status"><SelectValue /></SelectTrigger>
            <SelectContent>
              {lineStatusOptions.map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Date" htmlFor={`${id}-date`} changed={changed('date')} hint={line.date ? formatDate(line.date) : undefined}>
          <Input id={`${id}-date`} type="date" value={line.date || ''} onChange={(event) => setField('date', event.target.value)} disabled={!canEdit} />
        </Field>
        <Field label="Reference" htmlFor={`${id}-reference`} changed={changed('reference')} className="sm:col-span-2">
          <div className="flex items-center gap-2">
            <Input
              id={`${id}-reference`}
              value={line.reference || ''}
              onChange={(event) => setField('reference', event.target.value)}
              placeholder="Link or file path, e.g. https://… or Projects\\…\\MS-003.pdf"
              disabled={!canEdit}
              className="min-w-0 flex-1"
            />
            {String(line.reference || '').trim() ? (
              <Button type="button" variant="ghost" size="sm" onClick={() => onOpenReference?.(line.reference)}>
                <ExternalLink aria-hidden="true" />
                View
              </Button>
            ) : null}
          </div>
        </Field>
        <Field label="Note" htmlFor={`${id}-note`} changed={changed('note')} className="sm:col-span-2">
          <Textarea id={`${id}-note`} rows={2} value={line.note || ''} onChange={(event) => setField('note', event.target.value)} disabled={!canEdit} className="min-h-[60px]" />
        </Field>
      </div>

      {extraFields.length || canEdit ? (
        <div className="mt-3 space-y-2">
          {extraFields.map((field, fieldIndex) => (
            <div key={field.id} className="grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)_auto] items-center gap-2">
              <Input
                value={field.title || ''}
                onChange={(event) => setExtra(field.id, 'title', event.target.value)}
                placeholder="Field title"
                aria-label={`Extra field ${fieldIndex + 1} title`}
                disabled={!canEdit}
              />
              <Input
                value={field.content || ''}
                onChange={(event) => setExtra(field.id, 'content', event.target.value)}
                placeholder="Value"
                aria-label={`Extra field ${fieldIndex + 1} value`}
                disabled={!canEdit}
              />
              {canEdit ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Remove extra field ${field.title || fieldIndex + 1}`}
                  onClick={() => onChange({ ...line, extra_fields: extraFields.filter((entry) => entry.id !== field.id) })}
                >
                  <X aria-hidden="true" />
                </Button>
              ) : null}
            </div>
          ))}
          {canEdit ? (
            <Button
              type="button"
              variant="ghost"
              size="xs"
              className="text-[color:var(--brand-ink)]"
              onClick={() => onChange({ ...line, extra_fields: [...extraFields, newStageExtraField()] })}
            >
              <Plus aria-hidden="true" />
              Add field
            </Button>
          ) : null}
        </div>
      ) : null}
      {children}
    </div>
  );
}

export function StageEditPanel({
  open,
  onOpenChange,
  stage,
  stageNumber,
  lines = [],
  statusOptions = [],
  lineStatusOptions = [],
  supportsChildren = false,
  onSave,
  onCreateTask,
  onOpenReference,
  taskLineIds,
  canEdit = true,
  startWith,
  focusLineId,
  phone = false,
}) {
  const [draft, setDraft] = useState({ status: '', lines: [] });
  const [removed, setRemoved] = useState(null);
  // Sub-items show as one-line rows (name · status) until opened; new ones open straight away.
  const [openSubIds, setOpenSubIds] = useState(() => new Set());
  const toggleSub = (subId) => setOpenSubIds((prev) => {
    const next = new Set(prev);
    if (next.has(subId)) next.delete(subId); else next.add(subId);
    return next;
  });
  const focusIdRef = useRef(null);
  const originalStatus = displayStatus(stage?.status);
  const originalLines = useMemo(() => cloneStageLines(lines).filter((line) => !isBlankStageLine(line)), [lines]);
  const originalById = useMemo(() => {
    const map = new Map();
    originalLines.forEach((line) => {
      map.set(line.id, line);
      (line.sub_items || []).forEach((subItem) => map.set(subItem.id, subItem));
    });
    return map;
  }, [originalLines]);

  // A fresh draft every time the drawer opens (or opens on another stage).
  const stageId = stage?.id;
  useEffect(() => {
    if (!open || !stageId) return;
    const startLines = cloneStageLines(originalLines);
    focusIdRef.current = focusLineId || null;
    if (startWith === 'add-item' || (!startLines.length && canEdit)) {
      const fresh = newStageLine();
      startLines.push(fresh);
      if (startWith === 'add-item') focusIdRef.current = fresh.id;
    }
    setDraft({ status: originalStatus, lines: startLines });
    setRemoved(null);
    setOpenSubIds(new Set());
    // Only when the drawer opens: later changes to `lines` must not wipe the draft being edited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, stageId]);

  useEffect(() => {
    if (!open || !focusIdRef.current) return undefined;
    const timer = window.setTimeout(() => {
      const field = document.querySelector(`[data-stage-line-name="${focusIdRef.current}"]`);
      if (field) {
        field.scrollIntoView({ block: 'center' });
        field.focus({ preventScroll: true });
      }
      focusIdRef.current = null;
    }, 120);
    return () => window.clearTimeout(timer);
    // Runs after any draft change; it only acts when an add asked for focus (focusIdRef).
  }, [open, draft]);

  const dirty = open ? countStageDraftChanges({ status: originalStatus, lines: originalLines }, draft) : 0;

  const updateLine = (lineId, next, parentId = null) => setDraft((prev) => ({
    ...prev,
    lines: prev.lines.map((line) => {
      if (parentId) {
        return line.id === parentId
          ? { ...line, sub_items: (line.sub_items || []).map((subItem) => (subItem.id === lineId ? next : subItem)) }
          : line;
      }
      return line.id === lineId ? next : line;
    }),
  }));

  const insertAfter = (items, afterId, item) => {
    const index = items.findIndex((entry) => entry.id === afterId);
    return index < 0 ? [...items, item] : [...items.slice(0, index + 1), item, ...items.slice(index + 1)];
  };

  const addItem = (afterId = null) => {
    const fresh = newStageLine();
    focusIdRef.current = fresh.id;
    setDraft((prev) => ({ ...prev, lines: afterId ? insertAfter(prev.lines, afterId, fresh) : [...prev.lines, fresh] }));
  };
  const addSubItem = (parentId) => {
    const fresh = newStageLine();
    focusIdRef.current = fresh.id;
    setOpenSubIds((prev) => new Set([...prev, fresh.id]));
    setDraft((prev) => ({
      ...prev,
      lines: prev.lines.map((line) => (line.id === parentId ? { ...line, sub_items: [...(line.sub_items || []), fresh] } : line)),
    }));
  };
  const duplicate = (line, parentId = null) => {
    const copy = duplicateStageLine(line, { isSubItem: Boolean(parentId) });
    setDraft((prev) => ({
      ...prev,
      lines: parentId
        ? prev.lines.map((entry) => (entry.id === parentId ? { ...entry, sub_items: insertAfter(entry.sub_items || [], line.id, copy) } : entry))
        : insertAfter(prev.lines, line.id, copy),
    }));
  };
  const remove = (line, parentId = null) => {
    const list = parentId ? (draft.lines.find((entry) => entry.id === parentId)?.sub_items || []) : draft.lines;
    setRemoved({ line, parentId, index: Math.max(0, list.findIndex((entry) => entry.id === line.id)) });
    setDraft((prev) => ({
      ...prev,
      lines: parentId
        ? prev.lines.map((entry) => (entry.id === parentId ? { ...entry, sub_items: (entry.sub_items || []).filter((subItem) => subItem.id !== line.id) } : entry))
        : prev.lines.filter((entry) => entry.id !== line.id),
    }));
  };
  const restoreRemoved = () => {
    if (!removed) return;
    const { line, parentId, index } = removed;
    const put = (items) => [...items.slice(0, index), line, ...items.slice(index)];
    setDraft((prev) => ({
      ...prev,
      lines: parentId
        ? prev.lines.map((entry) => (entry.id === parentId ? { ...entry, sub_items: put(entry.sub_items || []) } : entry))
        : put(prev.lines),
    }));
    setRemoved(null);
  };

  const save = async () => {
    const linesChanged = countStageDraftChanges({ status: '', lines: originalLines }, { status: '', lines: draft.lines }) > 0;
    const statusChanged = draft.status !== originalStatus;
    if (!linesChanged && !statusChanged) return;
    await onSave?.({
      ...(statusChanged ? { status: draft.status } : {}),
      ...(linesChanged ? { lines: linesForSave(draft.lines) } : {}),
    });
  };

  const itemMenu = (line, index, parentId = null) => {
    const isSubItem = Boolean(parentId);
    const createTask = !isSubItem && onCreateTask && {
      label: 'Create task from item',
      icon: ListTodo,
      hint: taskLineIds?.has(line.id) ? 'Task exists' : undefined,
      disabled: !originalById.has(line.id),
      onSelect: () => onCreateTask(line),
      testId: `add-stage-task-${line.id}`,
    };
    // 28 #8: "Create task from item" follows Tasks (POST /tasks), so a role that may only VIEW Stages but edit
    // Tasks keeps it; the item edits follow Stages.
    if (!canEdit) return [createTask];
    return [
      !isSubItem && supportsChildren && { label: 'Add sub-item', icon: CornerDownRight, onSelect: () => addSubItem(line.id), testId: `add-stage-sub-line-${line.id}` },
      !isSubItem && { label: 'Add item below', icon: SquarePlus, onSelect: () => addItem(line.id), testId: `add-stage-line-${line.id}` },
      createTask,
      { label: isSubItem ? 'Duplicate sub-item' : 'Duplicate item', icon: Copy, onSelect: () => duplicate(line, parentId) },
      {
        label: isSubItem ? 'Remove sub-item' : 'Remove item',
        icon: Trash2,
        danger: true,
        onSelect: () => remove(line, parentId),
        testId: isSubItem ? `delete-stage-sub-line-${line.id}` : `delete-stage-line-${line.id}`,
      },
    ];
  };

  const visibleItems = draft.lines;
  const countText = `${visibleItems.filter((line) => !isBlankStageLine(line)).length} item${visibleItems.filter((line) => !isBlankStageLine(line)).length === 1 ? '' : 's'}`;

  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      width="lg"
      title={stage?.name || 'Stage'}
      subtitle={`${canEdit ? 'Update stage' : 'Stage'} ${stageNumber || ''} · ${countText}`.replace(/\s+·/, ' ·')}
      status={<StatusPill status={draft.status || originalStatus} domain="stage" />}
      dirty={dirty}
      onSave={canEdit ? save : undefined}
      saveLabel="Save stage"
      saveDisabled={!dirty}
      cancelLabel={canEdit ? 'Cancel' : 'Close'}
      confirmOptions={{
        title: 'Discard changes to this stage?',
        description: `${dirty} unsaved change${dirty === 1 ? '' : 's'} to ${stage?.name || 'this stage'} will be lost.`,
        confirmLabel: 'Discard changes',
        cancelLabel: 'Keep editing',
      }}
      className={cn(phone && 'max-sm:top-auto max-sm:h-[92dvh] max-sm:rounded-t-[22px] max-sm:border-l-0 max-sm:border-t max-sm:data-[state=open]:slide-in-from-bottom max-sm:data-[state=closed]:slide-out-to-bottom max-sm:data-[state=open]:slide-in-from-right-0 max-sm:data-[state=closed]:slide-out-to-right-0')}
      testId="stage-panel"
    >
      <SidePanelSection title="Stage status">
        <Field label="Status" htmlFor="stage-panel-status" changed={draft.status !== originalStatus} hint={draft.status !== originalStatus ? `was ${originalStatus}` : undefined}>
          <Select value={draft.status || originalStatus} onValueChange={(value) => setDraft((prev) => ({ ...prev, status: value }))} disabled={!canEdit}>
            <SelectTrigger
              id="stage-panel-status"
              aria-label="Stage status"
              className={cn(draft.status !== originalStatus && 'border-status-amber bg-[color:color-mix(in_srgb,var(--status-amber-bg)_40%,transparent)]')}
              data-testid="stage-panel-status"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
      </SidePanelSection>

      <SidePanelSection
        title={`Items · ${countText}`}
        actions={canEdit ? (
          <Button type="button" variant="ghost" size="xs" className="text-[color:var(--brand-ink)]" onClick={() => addItem()} data-testid="stage-panel-add-item">
            <Plus aria-hidden="true" />
            Add item
          </Button>
        ) : null}
      >
        {removed ? (
          <div className="flex items-center gap-2 rounded-[10px] border border-line bg-surface-2 px-3 py-2 text-[12.5px] text-ink-2" role="status" data-testid="stage-panel-removed">
            <Trash2 className="h-3.5 w-3.5 text-ink-3" aria-hidden="true" />
            <span className="min-w-0 flex-1 truncate">
              {removed.line.name ? `“${removed.line.name}”` : 'Item'} removed — it is deleted when you save.
            </span>
            <Button type="button" variant="ghost" size="xs" onClick={restoreRemoved}>
              <Undo2 aria-hidden="true" />
              Undo
            </Button>
          </div>
        ) : null}
        {visibleItems.length === 0 ? (
          <p className="rounded-[12px] border border-dashed border-[color:var(--line-dashed)] px-4 py-6 text-center text-[13px] text-ink-3">
            No items in this stage yet.
          </p>
        ) : null}
        {visibleItems.map((line, index) => (
          <ItemEditor
            key={line.id}
            line={line}
            index={index}
            original={originalById.get(line.id)}
            canEdit={canEdit}
            lineStatusOptions={lineStatusOptions}
            onChange={(next) => updateLine(line.id, next)}
            onOpenReference={onOpenReference}
            menuItems={itemMenu(line, index)}
          >
            {supportsChildren ? (
              <div className="mt-3 space-y-2 border-t border-line-2 pt-3">
                <div className="flex items-center gap-2">
                  <span className={FIELD_LABEL}>Sub-items</span>
                  <span className="text-[12px] text-ink-3">{(line.sub_items || []).length}</span>
                  {canEdit ? (
                    <Button type="button" variant="ghost" size="xs" className="ml-auto text-[color:var(--brand-ink)]" onClick={() => addSubItem(line.id)}>
                      <CornerDownRight aria-hidden="true" />
                      Add sub-item
                    </Button>
                  ) : null}
                </div>
                {(line.sub_items || []).map((subItem, subIndex) => (openSubIds.has(subItem.id) ? (
                  <div key={subItem.id} className="space-y-1">
                    <ItemEditor
                      line={subItem}
                      index={subIndex}
                      isSubItem
                      original={originalById.get(subItem.id)}
                      canEdit={canEdit}
                      lineStatusOptions={lineStatusOptions}
                      onChange={(next) => updateLine(subItem.id, next, line.id)}
                      onOpenReference={onOpenReference}
                      menuItems={itemMenu(subItem, subIndex, line.id)}
                    />
                    <Button type="button" variant="ghost" size="xs" onClick={() => toggleSub(subItem.id)} aria-expanded="true">
                      <ChevronUp aria-hidden="true" />
                      Close sub-item
                    </Button>
                  </div>
                ) : (
                  <button
                    key={subItem.id}
                    type="button"
                    onClick={() => toggleSub(subItem.id)}
                    aria-expanded="false"
                    className={cn(
                      'flex min-h-[44px] w-full items-center gap-2.5 rounded-[12px] border border-line bg-surface px-3 py-2 text-left transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      !originalById.get(subItem.id) && 'border-[color:color-mix(in_srgb,var(--status-amber)_50%,transparent)]',
                    )}
                    data-testid={`stage-sub-item-row-${subItem.id}`}
                  >
                    <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-ink">{subItem.name || `Sub-item ${subIndex + 1}`}</span>
                    {subItem.date ? <span className="shrink-0 text-[12px] text-ink-3">{formatDate(subItem.date)}</span> : null}
                    <StatusPill status={subItem.status || 'In Progress'} domain="stage_line" size="sm" />
                    <ChevronRight className="h-4 w-4 shrink-0 text-ink-4" aria-hidden="true" />
                  </button>
                )))}
              </div>
            ) : null}
          </ItemEditor>
        ))}
        {canEdit && visibleItems.length ? (
          <Button type="button" variant="outline" size="sm" className="self-start" onClick={() => addItem()}>
            <Plus aria-hidden="true" />
            Add item
          </Button>
        ) : null}
      </SidePanelSection>
    </SidePanel>
  );
}
